/*
 * Delivery estimate for the product page.
 *
 * Counts business days from the shop's own clock, not the visitor's: a shopper
 * whose device sits in another timezone must still see the window Portugal can
 * actually deliver. Orders placed after the cut-off ship the next business day,
 * so the countdown and the date it promises stay consistent with each other.
 */
(function () {
  const WEEKEND = [0, 6];

  function nowInZone(timeZone) {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).formatToParts(new Date());

      const value = (type) => parseInt(parts.find((part) => part.type === type).value, 10);

      // Some environments render midnight as hour 24.
      return new Date(
        value('year'),
        value('month') - 1,
        value('day'),
        value('hour') % 24,
        value('minute'),
        value('second')
      );
    } catch (error) {
      return new Date();
    }
  }

  function isBusinessDay(date) {
    return WEEKEND.indexOf(date.getDay()) === -1;
  }

  function addBusinessDays(date, days) {
    const result = new Date(date.getTime());
    let added = 0;

    while (added < days) {
      result.setDate(result.getDate() + 1);
      if (isBusinessDay(result)) added += 1;
    }

    return result;
  }

  function cutOffOn(date, cutOff) {
    const hours = Math.floor(cutOff);
    const minutes = Math.round((cutOff - hours) * 60);
    const target = new Date(date.getTime());
    target.setHours(hours, minutes, 0, 0);
    return target;
  }

  function formatDate(date, locale, withWeekday) {
    const options = withWeekday
      ? { weekday: 'short', day: '2-digit', month: '2-digit' }
      : { day: '2-digit', month: '2-digit' };

    try {
      return date.toLocaleDateString(locale || 'pt-PT', options);
    } catch (error) {
      return date.toLocaleDateString('pt-PT', options);
    }
  }

  function render(root) {
    const timeZone = root.dataset.timezone || 'Europe/Lisbon';
    const locale = root.dataset.locale || 'pt-PT';
    const cutOff = parseFloat(root.dataset.cutOff);
    const daysMin = parseInt(root.dataset.daysMin, 10);
    const daysMax = parseInt(root.dataset.daysMax, 10);

    const now = nowInZone(timeZone);

    // Dispatch happens today only while the cut-off is still ahead and today is
    // a working day; otherwise the clock starts on the next business day.
    let dispatch = new Date(now.getTime());
    if (!isBusinessDay(dispatch) || (!isNaN(cutOff) && now >= cutOffOn(dispatch, cutOff))) {
      dispatch = addBusinessDays(dispatch, 1);
    }

    const dateTarget = root.querySelector('[data-ship-date]');
    if (dateTarget && !isNaN(daysMax)) {
      const latest = addBusinessDays(dispatch, daysMax);
      let text;

      if (isNaN(daysMin) || daysMin <= 0) {
        text = (root.dataset.labelUntil || 'até') + ' ' + formatDate(latest, locale, true);
      } else if (daysMin === daysMax) {
        text = (root.dataset.labelOn || 'na') + ' ' + formatDate(latest, locale, true);
      } else {
        const earliest = addBusinessDays(dispatch, daysMin);
        text =
          (root.dataset.labelBetween || 'entre') +
          ' ' +
          formatDate(earliest, locale, false) +
          ' e ' +
          formatDate(latest, locale, true);
      }

      dateTarget.textContent = text;
    }

    return { now: now, cutOff: cutOff };
  }

  function startCountdown(root) {
    const target = root.querySelector('[data-ship-countdown]');
    const cutOff = parseFloat(root.dataset.cutOff);
    if (!target || isNaN(cutOff)) return;

    const tick = () => {
      const now = nowInZone(root.dataset.timezone || 'Europe/Lisbon');
      let deadline = cutOffOn(now, cutOff);

      if (deadline <= now) {
        // Past the cut-off: aim at the next one and move the promised date with
        // it, so the two never contradict each other.
        deadline = cutOffOn(addBusinessDays(now, 1), cutOff);
        render(root);
      }

      const distance = deadline.getTime() - now.getTime();
      const hours = Math.floor(distance / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));

      target.textContent = hours + 'h ' + minutes + 'm';
    };

    tick();
    setInterval(tick, 30000);
  }

  function init() {
    document.querySelectorAll('[data-ship-estimate]').forEach((root) => {
      if (root.dataset.shipReady === 'true') return;
      root.dataset.shipReady = 'true';
      render(root);
      startCountdown(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', init);
})();
